import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userService, contractService, propertyService } from '../services/apiService';
import { queryKeys } from '../utils/queryClient';
import { ErrorMessage } from '../components/ui/ErrorHandling';
import { ListSkeleton } from '../components/ui/LoadingSkeletons';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../utils/api';
import {
  PageTitle, KPI, KPIStrip, Badge, Button, Card,
  Table, THead, TBody, TR, TH, Row, Cell, EmptyState, Icons,
} from '@/shared/ui';

const INPUT_CLS = 'w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 disabled:bg-stone-50';
const LABEL_CLS = 'block text-xs font-medium text-stone-600 mb-1';

const Renters = () => {
  const { t } = useTranslation(['renters', 'common']);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRenter, setSelectedRenter] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [expandedRooms, setExpandedRooms] = useState({});
  const [viewMode, setViewMode] = useState('building');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();

  const { data: renters = [], isLoading, error } = useQuery({
    queryKey: queryKeys.users.renters(),
    queryFn: () => userService.getRenters(),
    select: (data) => Array.isArray(data) ? data : [],
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings'],
    queryFn: () => propertyService.getBuildings(),
    select: (data) => Array.isArray(data) ? data : [],
  });

  const { data: allRooms = [] } = useQuery({
    queryKey: ['all-rooms'],
    queryFn: async () => {
      const roomPromises = buildings.map(b => propertyService.getBuildingRooms(b.id));
      const roomArrays = await Promise.all(roomPromises);
      return roomArrays.flat();
    },
    enabled: buildings.length > 0,
    select: (data) => Array.isArray(data) ? data : [],
  });

  const filteredRenters = renters.filter(renter =>
    renter.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    renter.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    renter.phone?.includes(searchTerm) ||
    renter.room_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    renter.building_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const buildingRoomHierarchy = useMemo(() => {
    const hierarchy = {};
    buildings.forEach(building => {
      hierarchy[building.id] = { ...building, rooms: {}, totalRenters: 0, occupiedRooms: 0, totalRooms: building.total_rooms || 0 };
    });
    hierarchy['unassigned'] = { id: 'unassigned', name: 'Unassigned Renters', address: 'Renters without room assignment', rooms: {}, totalRenters: 0, occupiedRooms: 0, totalRooms: 0 };
    allRooms.forEach(room => {
      const buildingId = room.building_id;
      if (hierarchy[buildingId]) {
        hierarchy[buildingId].rooms[room.id] = { ...room, renters: [] };
      }
    });
    filteredRenters.forEach(renter => {
      if (renter.room_id && renter.building_id && hierarchy[renter.building_id]) {
        const building = hierarchy[renter.building_id];
        if (!building.rooms[renter.room_id]) {
          building.rooms[renter.room_id] = { id: renter.room_id, room_number: renter.room_number, floor: renter.floor || 1, status: 'occupied', monthly_rent: renter.monthly_rent, renters: [] };
        }
        building.rooms[renter.room_id].renters.push(renter);
        building.totalRenters++;
        if (building.rooms[renter.room_id].renters.length === 1) building.occupiedRooms++;
      } else {
        if (!hierarchy['unassigned'].rooms['unassigned']) {
          hierarchy['unassigned'].rooms['unassigned'] = { id: 'unassigned', room_number: 'Not Assigned', renters: [] };
        }
        hierarchy['unassigned'].rooms['unassigned'].renters.push(renter);
        hierarchy['unassigned'].totalRenters++;
      }
    });
    return hierarchy;
  }, [buildings, allRooms, filteredRenters]);

  const roomBasedView = useMemo(() => {
    const rooms = [];
    Object.entries(buildingRoomHierarchy).forEach(([buildingId, building]) => {
      if (buildingId === 'unassigned') return;
      Object.values(building.rooms).forEach(room => {
        rooms.push({ ...room, building_id: buildingId, building_name: building.name, building_address: building.address });
      });
    });
    return rooms.sort((a, b) => {
      if (a.building_name !== b.building_name) return a.building_name.localeCompare(b.building_name);
      if (a.floor !== b.floor) return (a.floor || 0) - (b.floor || 0);
      return (a.room_number || '').localeCompare(b.room_number || '');
    });
  }, [buildingRoomHierarchy]);

  const toggleBuilding = (buildingId) => setExpandedBuildings(prev => ({ ...prev, [buildingId]: !prev[buildingId] }));
  const toggleRoom = (roomId) => setExpandedRooms(prev => ({ ...prev, [roomId]: !prev[roomId] }));
  const expandAll = () => {
    const expanded = {};
    Object.keys(buildingRoomHierarchy).forEach(id => { expanded[id] = true; });
    setExpandedBuildings(expanded);
  };
  const collapseAll = () => { setExpandedBuildings({}); setExpandedRooms({}); };

  const stats = {
    totalRenters: renters.length,
    activeRenters: renters.filter(r => r.is_active).length,
    assignedRenters: renters.filter(r => r.room_id).length,
    unassignedRenters: renters.filter(r => !r.room_id).length,
  };

  const toggleSelect = (id) => setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.size === filteredRenters.length ? new Set() : new Set(filteredRenters.map(r => r.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    let successCount = 0, failCount = 0;
    for (const id of selectedIds) {
      try { await api.delete(`/api/renters/${id}`); successCount++; } catch { failCount++; }
    }
    setBulkDeleting(false);
    setShowBulkDeleteConfirm(false);
    setSelectedIds(new Set());
    queryClient.invalidateQueries({ queryKey: queryKeys.users.renters() });
    if (failCount === 0) toast.success(`Successfully deleted ${successCount} renter(s)`);
    else toast.error(`Deleted ${successCount}, failed ${failCount}`);
  };

  const viewActions = (
    <div className="flex flex-wrap gap-2">
      <div className="flex p-1 rounded-xl bg-stone-100 border border-stone-200 gap-1">
        {[
          { id: 'building', icon: <Icons.Building size={14} />, label: 'Building' },
          { id: 'room', icon: <Icons.Grid size={14} />, label: 'Rooms' },
          { id: 'list', icon: <Icons.List size={14} />, label: 'List' },
        ].map(v => (
          <button
            key={v.id}
            onClick={() => { setViewMode(v.id); setSelectedIds(new Set()); setShowBulkDeleteConfirm(false); }}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${viewMode === v.id ? 'bg-white text-teal-700 shadow-sm' : 'text-stone-500 hover:text-stone-900'}`}
          >
            {v.icon}{v.label}
          </button>
        ))}
      </div>
      {viewMode === 'building' && (
        <div className="flex p-1 rounded-xl bg-stone-100 border border-stone-200 gap-1">
          <button onClick={expandAll} className="px-3 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-white rounded-lg transition-all">Expand All</button>
          <button onClick={collapseAll} className="px-3 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-white rounded-lg transition-all">Collapse</button>
        </div>
      )}
      <Button kind="primary" iconLeft={<Icons.Plus />} onClick={() => setShowAddModal(true)}>Add Renter</Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle title={t('renters:title')} subtitle={t('renters:subtitle')} icon={<Icons.Users />} actions={viewActions} />

      <div className="space-y-3">
        <div className="relative">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, phone, room, building..."
            className="w-full h-10 pl-9 pr-4 text-sm border border-stone-300 rounded-lg bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
          />
        </div>
        <KPIStrip columns={4}>
          <KPI label="Total Renters" value={stats.totalRenters} icon={<Icons.Users />} accent="neutral" />
          <KPI label="Active" value={stats.activeRenters} icon={<Icons.CheckCircle2 />} accent="success" />
          <KPI label="Assigned" value={stats.assignedRenters} icon={<Icons.Home />} accent="primary" />
          <KPI label="Unassigned" value={stats.unassignedRenters} icon={<Icons.AlertCircle />} accent="warning" />
        </KPIStrip>
      </div>

      {isLoading ? (
        <ListSkeleton items={5} />
      ) : error ? (
        <ErrorMessage error={error} onRetry={() => queryClient.invalidateQueries({ queryKey: queryKeys.users.renters() })} />
      ) : filteredRenters.length === 0 ? (
        <EmptyState icon={<Icons.Users size={40} />} title="No renters found" />
      ) : viewMode === 'building' ? (
        <div className="space-y-4">
          {Object.entries(buildingRoomHierarchy)
            .filter(([id, building]) => building.totalRenters > 0 || (id !== 'unassigned' && Object.keys(building.rooms).length > 0))
            .sort((a, b) => { if (a[0] === 'unassigned') return 1; if (b[0] === 'unassigned') return -1; return a[1].name.localeCompare(b[1].name); })
            .map(([buildingId, building]) => {
              const roomsByFloor = Object.values(building.rooms).reduce((acc, room) => {
                const floor = room.floor || 0;
                if (!acc[floor]) acc[floor] = [];
                acc[floor].push(room);
                return acc;
              }, {});
              const floors = Object.keys(roomsByFloor).sort((a, b) => Number(a) - Number(b));

              return (
                <div key={buildingId} className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                  <div
                    onClick={() => toggleBuilding(buildingId)}
                    className={`cursor-pointer px-4 py-3 flex items-center justify-between ${buildingId === 'unassigned' ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 'bg-gradient-to-r from-teal-600 to-teal-700'} text-white`}
                  >
                    <div className="flex items-center gap-3">
                      {expandedBuildings[buildingId] ? <Icons.ChevronDown size={18} /> : <Icons.ChevronRight size={18} />}
                      <Icons.Building size={22} />
                      <div>
                        <h2 className="text-lg font-bold">{building.name}</h2>
                        {building.address && <p className="text-xs opacity-80">{building.address}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center px-3 py-1 bg-white/20 rounded-lg">
                        <span className="font-bold">{building.totalRenters}</span><span className="opacity-80 ml-1">renters</span>
                      </div>
                      {buildingId !== 'unassigned' && (
                        <div className="text-center px-3 py-1 bg-white/20 rounded-lg">
                          <span className="font-bold">{building.occupiedRooms}/{Object.keys(building.rooms).length}</span><span className="opacity-80 ml-1">rooms</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {expandedBuildings[buildingId] && (
                    <div className="bg-stone-50">
                      {buildingId === 'unassigned' ? (
                        <div className="p-4">
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                            {Object.values(building.rooms).flatMap(r => r.renters).map(renter => (
                              <UnassignedRenterCard key={renter.id} renter={renter} onSelect={setSelectedRenter} />
                            ))}
                          </div>
                        </div>
                      ) : Object.keys(building.rooms).length === 0 ? (
                        <div className="text-center py-8 text-stone-500">
                          <Icons.Home size={40} className="mx-auto mb-2 text-stone-300" />
                          <p>No rooms in this building</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-stone-200">
                          {floors.map(floor => {
                            const floorRooms = roomsByFloor[floor].sort((a, b) => (a.room_number || '').localeCompare(b.room_number || ''));
                            const occupiedCount = floorRooms.filter(r => r.renters?.length > 0).length;
                            const renterCount = floorRooms.reduce((sum, r) => sum + (r.renters?.length || 0), 0);
                            return (
                              <div key={floor} className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                                      <span className="text-teal-700 font-bold text-sm">{floor === '0' ? 'G' : floor}</span>
                                    </div>
                                    <span className="font-semibold text-stone-700">{floor === '0' ? 'Ground Floor' : `Floor ${floor}`}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge tone="info">{occupiedCount}/{floorRooms.length} rooms</Badge>
                                    <Badge tone="success">{renterCount} renters</Badge>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                                  {floorRooms.map(room => (
                                    <RoomCardCompact key={room.id} room={room} onSelectRenter={setSelectedRenter} />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ) : viewMode === 'room' ? (
        <div className="space-y-6">
          {Object.entries(
            roomBasedView.reduce((acc, room) => {
              if (!acc[room.building_name]) acc[room.building_name] = { address: room.building_address, floors: {} };
              const floor = room.floor || 0;
              if (!acc[room.building_name].floors[floor]) acc[room.building_name].floors[floor] = [];
              acc[room.building_name].floors[floor].push(room);
              return acc;
            }, {})
          ).map(([buildingName, data]) => {
            const allBuildingRooms = Object.values(data.floors).flat();
            const occupiedCount = allBuildingRooms.filter(r => r.renters?.length > 0).length;
            const floorNumbers = Object.keys(data.floors).sort((a, b) => Number(a) - Number(b));
            return (
              <div key={buildingName} className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icons.Building size={22} />
                    <div>
                      <h2 className="font-bold">{buildingName}</h2>
                      {data.address && <p className="text-xs opacity-80">{data.address}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="px-2 py-1 bg-white/20 rounded-lg">{occupiedCount}/{allBuildingRooms.length} occupied</span>
                    <span className="px-2 py-1 bg-white/20 rounded-lg">{floorNumbers.length} floors</span>
                  </div>
                </div>
                <div className="divide-y divide-stone-100">
                  {floorNumbers.map(floor => {
                    const floorRooms = data.floors[floor].sort((a, b) => (a.room_number || '').localeCompare(b.room_number || ''));
                    const floorOccupied = floorRooms.filter(r => r.renters?.length > 0).length;
                    return (
                      <div key={floor} className="p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 bg-teal-100 rounded flex items-center justify-center text-xs font-bold text-teal-700">{floor === '0' ? 'G' : floor}</span>
                          <span className="text-sm font-medium text-stone-600">{floor === '0' ? 'Ground' : `Floor ${floor}`}</span>
                          <span className="text-xs text-stone-400 ml-auto">{floorOccupied}/{floorRooms.length}</span>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                          {floorRooms.map(room => <RoomCardCompact key={room.id} room={room} onSelectRenter={setSelectedRenter} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {buildingRoomHierarchy['unassigned']?.totalRenters > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icons.XCircle size={22} />
                  <div>
                    <h2 className="font-bold">{t('renters:unassigned')}</h2>
                    <p className="text-xs opacity-80">Renters without room assignment</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-white/20 rounded-lg text-sm font-bold">{buildingRoomHierarchy['unassigned'].totalRenters}</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {Object.values(buildingRoomHierarchy['unassigned'].rooms).flatMap(r => r.renters).map(renter => (
                    <UnassignedRenterCard key={renter.id} renter={renter} onSelect={setSelectedRenter} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-0">
          {selectedIds.size > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-t-2xl px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-teal-800">{selectedIds.size} renter{selectedIds.size > 1 ? 's' : ''} selected</span>
                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-teal-600 hover:text-teal-800 underline">Clear selection</button>
              </div>
              <div className="flex items-center gap-2">
                {showBulkDeleteConfirm ? (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                    <Icons.Alert size={14} className="text-red-600" />
                    <span className="text-xs font-medium text-red-800">Delete {selectedIds.size} renter{selectedIds.size > 1 ? 's' : ''}?</span>
                    <button onClick={handleBulkDelete} disabled={bulkDeleting} className="px-2.5 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 disabled:opacity-50">
                      {bulkDeleting ? 'Deleting...' : 'Confirm'}
                    </button>
                    <button onClick={() => setShowBulkDeleteConfirm(false)} className="px-2.5 py-1 bg-white text-stone-700 text-xs font-medium rounded border hover:bg-stone-50">Cancel</button>
                  </div>
                ) : (
                  <Button kind="danger" size="sm" iconLeft={<Icons.Trash />} onClick={() => setShowBulkDeleteConfirm(true)}>Delete Selected</Button>
                )}
              </div>
            </div>
          )}
          <Card className={selectedIds.size > 0 ? 'rounded-t-none border-t-0' : ''}>
            <Table>
              <THead>
                <TR>
                  <TH className="w-10">
                    <input type="checkbox" checked={filteredRenters.length > 0 && selectedIds.size === filteredRenters.length}
                      onChange={toggleSelectAll} className="h-4 w-4 rounded accent-teal-600 cursor-pointer" title="Select all" />
                  </TH>
                  <TH>Renter</TH>
                  <TH>Contact</TH>
                  <TH>Building</TH>
                  <TH>Room</TH>
                  <TH>Contract</TH>
                  <TH>Status</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {filteredRenters.map((renter) => (
                  <Row key={renter.id} className={selectedIds.has(renter.id) ? 'bg-teal-50/50' : ''}>
                    <Cell>
                      <input type="checkbox" checked={selectedIds.has(renter.id)} onChange={() => toggleSelect(renter.id)}
                        className="h-4 w-4 rounded accent-teal-600 cursor-pointer" />
                    </Cell>
                    <Cell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-teal-600 font-semibold">{renter.full_name?.charAt(0) || 'R'}</span>
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{renter.full_name}</p>
                          {renter.is_primary && <Badge tone="warning" className="text-[10px]">Primary</Badge>}
                        </div>
                      </div>
                    </Cell>
                    <Cell>
                      <div className="text-sm">
                        <p className="text-stone-900">{renter.email || '-'}</p>
                        <p className="text-stone-500">{renter.phone || '-'}</p>
                      </div>
                    </Cell>
                    <Cell>{renter.building_name || <span className="text-stone-400 text-sm">Not assigned</span>}</Cell>
                    <Cell>{renter.room_number || <span className="text-stone-400 text-sm">Not assigned</span>}</Cell>
                    <Cell>
                      {renter.contract_number ? (
                        <div>
                          <p className="text-stone-900 text-sm">{renter.contract_number}</p>
                          <p className="text-stone-500 text-xs">{(renter.monthly_rent || 0).toLocaleString('vi-VN')}₫/mo</p>
                        </div>
                      ) : <span className="text-stone-400 text-sm">No contract</span>}
                    </Cell>
                    <Cell>
                      <Badge tone={renter.is_active ? 'success' : 'neutral'}>{renter.is_active ? 'Active' : 'Inactive'}</Badge>
                    </Cell>
                    <Cell>
                      <button onClick={() => setSelectedRenter(renter)} className="text-teal-600 hover:text-teal-900 text-sm font-medium">View Details</button>
                    </Cell>
                  </Row>
                ))}
              </TBody>
            </Table>
          </Card>
        </div>
      )}

      {selectedRenter && (
        <RenterDetailsModal
          renter={selectedRenter}
          onClose={() => setSelectedRenter(null)}
          onDelete={() => queryClient.invalidateQueries({ queryKey: queryKeys.users.renters() })}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: queryKeys.users.renters() })}
        />
      )}
      {showAddModal && (
        <AddRenterModal onClose={() => setShowAddModal(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: queryKeys.users.renters() })} />
      )}
    </div>
  );
};

const UnassignedRenterCard = ({ renter, onSelect }) => (
  <div onClick={() => onSelect(renter)}
    className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white p-3 hover:shadow-lg hover:border-orange-300 cursor-pointer transition-all group">
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-orange-300 transition-colors">
        <span className="text-orange-700 font-bold text-sm">{renter.full_name?.charAt(0) || 'R'}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-stone-900 truncate">{renter.full_name}</p>
        <p className="text-xs text-stone-500 truncate">{renter.phone || renter.email || 'No contact'}</p>
      </div>
    </div>
    <div className="mt-2 flex items-center justify-between">
      {renter.is_active
        ? <span className="flex items-center gap-1 text-xs text-green-600"><Icons.CheckCircle2 size={12} />Active</span>
        : <span className="flex items-center gap-1 text-xs text-stone-400"><Icons.XCircle size={12} />Inactive</span>
      }
      <span className="text-xs text-orange-500 font-medium">No room</span>
    </div>
  </div>
);

const RoomCardCompact = ({ room, onSelectRenter }) => {
  const hasRenters = room.renters && room.renters.length > 0;
  const renterCount = room.renters?.length || 0;
  return (
    <div
      className={`rounded-lg border overflow-hidden transition-all hover:shadow-md cursor-pointer ${
        hasRenters ? 'border-teal-300 bg-teal-50 hover:bg-teal-100' : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50'
      }`}
      onClick={() => hasRenters && renterCount === 1 && onSelectRenter(room.renters[0])}
    >
      <div className={`px-2 py-1.5 flex items-center justify-between ${hasRenters ? 'bg-teal-200' : 'bg-stone-100'}`}>
        <span className={`font-bold text-sm ${hasRenters ? 'text-teal-800' : 'text-stone-600'}`}>{room.room_number || 'Room'}</span>
        {hasRenters
          ? <span className="text-xs bg-teal-600 text-white px-1.5 py-0.5 rounded-full font-medium">{renterCount}</span>
          : <span className="text-xs text-green-600 font-medium">Free</span>
        }
      </div>
      <div className="px-2 py-1.5">
        {hasRenters ? (
          <div className="space-y-1">
            {room.renters.slice(0, 2).map((renter) => (
              <div key={renter.id} onClick={(e) => { e.stopPropagation(); onSelectRenter(renter); }}
                className="flex items-center gap-1.5 hover:bg-white rounded px-1 py-0.5 cursor-pointer transition">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${renter.is_active ? 'bg-green-200 text-green-700' : 'bg-stone-200 text-stone-500'}`}>
                  {renter.full_name?.charAt(0) || 'R'}
                </div>
                <span className="text-xs text-stone-700 truncate flex-1" title={renter.full_name}>{renter.full_name?.split(' ')[0] || 'Renter'}</span>
                {renter.is_primary && <span className="text-[9px] bg-amber-200 text-amber-700 px-1 rounded">★</span>}
              </div>
            ))}
            {renterCount > 2 && <div className="text-[10px] text-teal-600 font-medium text-center">+{renterCount - 2} more</div>}
          </div>
        ) : (
          <div className="text-center py-1"><span className="text-xs text-stone-400">Available</span></div>
        )}
      </div>
      {room.monthly_rent && (
        <div className={`px-2 py-1 text-center text-[10px] font-semibold border-t ${hasRenters ? 'bg-green-100 text-green-700 border-green-200' : 'bg-stone-50 text-stone-500 border-stone-100'}`}>
          ${room.monthly_rent}/mo
        </div>
      )}
    </div>
  );
};

const RenterDetailsModal = ({ renter, onClose, onDelete, onUpdate }) => {
  const { t } = useTranslation(['renters', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [renterProfile, setRenterProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [idImages, setIdImages] = useState({ front: null, back: null });
  const [ocrLoading, setOcrLoading] = useState({ front: false, back: false });
  const editFrontRef = React.useRef(null);
  const editBackRef = React.useRef(null);
  const navigate = useNavigate();

  const [userForm, setUserForm] = useState({ first_name: renter.first_name || '', last_name: renter.last_name || '', email: renter.email || '', phone: renter.phone || '' });
  const [profileForm, setProfileForm] = useState({
    id_number: '', id_issue_date: '', id_issue_place: '', date_of_birth: '', gender: '',
    address: '', place_of_origin: '', permanent_address: '', occupation: '', workplace: '',
    emergency_contact_name: renter.emergency_contact_name || '',
    emergency_contact_phone: renter.emergency_contact_phone || '',
    emergency_contact_relationship: renter.emergency_contact_relationship || '',
    notes: '',
  });

  React.useEffect(() => {
    setProfileLoading(true);
    api.get(`/api/renters/${renter.id}/profile`)
      .then(res => {
        const p = res.data;
        setRenterProfile(p);
        setProfileForm({
          id_number: p.id_number || '', id_issue_date: p.id_issue_date || '', id_issue_place: p.id_issue_place || '',
          date_of_birth: p.date_of_birth || '', gender: p.gender || '', address: p.address || '',
          place_of_origin: p.place_of_origin || '', permanent_address: p.permanent_address || '',
          occupation: p.occupation || '', workplace: p.workplace || '',
          emergency_contact_name: p.emergency_contact_name || renter.emergency_contact_name || '',
          emergency_contact_phone: p.emergency_contact_phone || renter.emergency_contact_phone || '',
          emergency_contact_relationship: p.emergency_contact_relationship || renter.emergency_contact_relationship || '',
          notes: p.notes || '',
        });
      })
      .catch(() => setRenterProfile(null))
      .finally(() => setProfileLoading(false));
  }, [renter.id]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/api/users/${renter.id}`, { ...userForm, email: userForm.email || null, phone: userForm.phone || null });
      const profilePayload = { ...profileForm };
      if (idImages.front) profilePayload.id_front_image = idImages.front;
      if (idImages.back) profilePayload.id_back_image = idImages.back;
      await api.put(`/api/renters/${renter.id}/profile`, profilePayload);
      try { const refreshed = await api.get(`/api/renters/${renter.id}/profile`); setRenterProfile(refreshed.data); } catch { }
      toast.success(t('renters:toast.infoUpdated'));
      setIsEditing(false);
      setIdImages({ front: null, back: null });
      onUpdate?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Failed to update renter');
    } finally { setSaving(false); }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setUserForm({ first_name: renter.first_name || '', last_name: renter.last_name || '', email: renter.email || '', phone: renter.phone || '' });
    if (renterProfile) {
      setProfileForm({
        id_number: renterProfile.id_number || '', id_issue_date: renterProfile.id_issue_date || '', id_issue_place: renterProfile.id_issue_place || '',
        date_of_birth: renterProfile.date_of_birth || '', gender: renterProfile.gender || '', address: renterProfile.address || '',
        place_of_origin: renterProfile.place_of_origin || '', permanent_address: renterProfile.permanent_address || '',
        occupation: renterProfile.occupation || '', workplace: renterProfile.workplace || '',
        emergency_contact_name: renterProfile.emergency_contact_name || '', emergency_contact_phone: renterProfile.emergency_contact_phone || '',
        emergency_contact_relationship: renterProfile.emergency_contact_relationship || '', notes: renterProfile.notes || '',
      });
    }
    setIdImages({ front: null, back: null });
  };

  const handleEditIdImage = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('renters:toast.imageRequired')); return; }
    const reader = new FileReader();
    reader.onloadend = () => setIdImages(prev => ({ ...prev, [side]: reader.result }));
    reader.readAsDataURL(file);
    setOcrLoading(prev => ({ ...prev, [side]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('side', side);
      const res = await api.post('/api/ocr/vietnamese-id', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setProfileForm(prev => ({ ...prev, id_number: d.id_number || prev.id_number, id_issue_place: d.place_of_origin || prev.id_issue_place }));
        if (d.issue_date) {
          const parts = d.issue_date.split(/[\/\-\.]/);
          if (parts.length === 3) setProfileForm(prev => ({ ...prev, id_issue_date: `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}` }));
        }
        toast.success(`${side === 'front' ? 'Front' : 'Back'} side OCR extracted`);
      }
    } catch { } finally { setOcrLoading(prev => ({ ...prev, [side]: false })); }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await api.delete(`/api/renters/${renter.id}`);
      toast.success(`${renter.full_name} has been deleted`);
      onDelete?.(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || t('renters:toast.deleteFailed')); }
    finally { setDeleteLoading(false); setShowDeleteConfirm(false); }
  };

  const handleReactivate = async () => {
    try {
      setReactivateLoading(true);
      await api.post(`/api/users/${renter.id}/reactivate`);
      toast.success(`${renter.full_name} has been reactivated`);
      onUpdate?.(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || t('renters:toast.reactivateFailed')); }
    finally { setReactivateLoading(false); }
  };

  const handleViewContract = () => { navigate(renter.contract_id ? `/manager/contracts?contract_id=${renter.contract_id}` : '/manager/contracts'); onClose(); };
  const handleViewTickets = () => { navigate(`/manager/tickets?renter=${renter.id}`); onClose(); };
  const handleViewPayments = () => { navigate(`/manager/finance?renter=${renter.id}`); onClose(); };

  const p = renterProfile;

  return (
    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden ring-1 ring-black/5 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-teal-50 to-teal-100">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center ring-2 ring-teal-200">
              <span className="text-teal-600 font-bold text-xl">{renter.full_name?.charAt(0) || 'R'}</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-stone-900">{renter.full_name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm text-stone-500">ID: #{renter.id}</span>
                {renter.is_primary && <Badge tone="warning">Primary</Badge>}
                {renter.is_active ? (
                  <Badge tone="success">Active</Badge>
                ) : (
                  <>
                    <Badge tone="danger">Deactivated</Badge>
                    <button onClick={handleReactivate} disabled={reactivateLoading}
                      className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-medium hover:bg-green-700 disabled:opacity-50">
                      {reactivateLoading ? 'Reactivating...' : 'Reactivate'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <Button kind="secondary" size="sm" iconLeft={<Icons.Edit />} onClick={() => setIsEditing(true)}>Edit</Button>
            ) : (
              <>
                <Button kind="primary" size="sm" loading={saving} onClick={handleSave}>Save Changes</Button>
                <Button kind="secondary" size="sm" onClick={handleCancelEdit}>Cancel</Button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-stone-200 rounded-lg ml-1">
              <Icons.X size={18} className="text-stone-400" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 flex-1">
          {/* Personal Information */}
          <section>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Icons.Users size={14} className="text-teal-500" />Personal Information
            </h3>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-4">
                <div><label className={LABEL_CLS}>First Name</label><input type="text" value={userForm.first_name} onChange={e => setUserForm(f => ({ ...f, first_name: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>Last Name</label><input type="text" value={userForm.last_name} onChange={e => setUserForm(f => ({ ...f, last_name: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>Email</label><input type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>Phone</label><input type="text" value={userForm.phone} onChange={e => setUserForm(f => ({ ...f, phone: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>Date of Birth</label><input type="date" value={profileForm.date_of_birth} onChange={e => setProfileForm(f => ({ ...f, date_of_birth: e.target.value }))} className={INPUT_CLS} /></div>
                <div>
                  <label className={LABEL_CLS}>Gender</label>
                  <select value={profileForm.gender} onChange={e => setProfileForm(f => ({ ...f, gender: e.target.value }))} className={INPUT_CLS}>
                    <option value="">Select...</option><option value="male">Male</option><option value="female">Female</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {[['First Name', renter.first_name], ['Last Name', renter.last_name], ['Email', renter.email], ['Phone', renter.phone], ['Date of Birth', p?.date_of_birth], ['Gender', p?.gender]].map(([label, val]) => (
                  <div key={label} className="flex justify-between py-1.5">
                    <span className="text-sm text-stone-500">{label}</span>
                    <span className="text-sm text-stone-900 font-medium capitalize">{val || 'N/A'}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ID Information */}
          <section>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Icons.CreditCard size={14} className="text-teal-500" />ID Information
            </h3>
            {profileLoading ? <div className="text-sm text-stone-400">Loading profile...</div> : isEditing ? (
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div><label className={LABEL_CLS}>ID Number</label><input type="text" value={profileForm.id_number} onChange={e => setProfileForm(f => ({ ...f, id_number: e.target.value }))} className={INPUT_CLS} placeholder="e.g. 001234567890" /></div>
                  <div><label className={LABEL_CLS}>Issue Date</label><input type="date" value={profileForm.id_issue_date} onChange={e => setProfileForm(f => ({ ...f, id_issue_date: e.target.value }))} className={INPUT_CLS} /></div>
                  <div><label className={LABEL_CLS}>Issue Place</label><input type="text" value={profileForm.id_issue_place} onChange={e => setProfileForm(f => ({ ...f, id_issue_place: e.target.value }))} className={INPUT_CLS} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(['front', 'back']).map(side => (
                    <div key={side}>
                      <label className={LABEL_CLS}>ID {side === 'front' ? 'Front' : 'Back'} Side</label>
                      {(idImages[side] || p?.[`id_${side}_image`]) ? (
                        <div className="relative group">
                          <img src={idImages[side] || (p[`id_${side}_image`].startsWith('data:') ? p[`id_${side}_image`] : `data:image/jpeg;base64,${p[`id_${side}_image`]}`)}
                            alt={`ID ${side}`} className="w-full h-36 object-contain rounded-lg border border-teal-200 bg-stone-50" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                            <button type="button" onClick={() => (side === 'front' ? editFrontRef : editBackRef).current?.click()} className="px-3 py-1.5 bg-white text-stone-800 text-xs font-medium rounded-lg hover:bg-stone-100">Replace</button>
                            <button type="button" onClick={() => setIdImages(prev => ({ ...prev, [side]: null }))} className="px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600">Remove</button>
                          </div>
                          {ocrLoading[side] && <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center"><Icons.Spinner size={22} className="animate-spin text-teal-600" /></div>}
                          {idImages[side] && <span className="absolute top-1 right-1 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">New</span>}
                        </div>
                      ) : (
                        <button type="button" onClick={() => (side === 'front' ? editFrontRef : editBackRef).current?.click()} disabled={ocrLoading[side]}
                          className="w-full h-36 border-2 border-dashed border-teal-300 rounded-lg flex flex-col items-center justify-center gap-1.5 hover:border-teal-500 hover:bg-teal-50 transition disabled:opacity-50">
                          {ocrLoading[side] ? (
                            <><Icons.Spinner size={22} className="animate-spin text-teal-600" /><span className="text-xs text-stone-600">Processing OCR...</span></>
                          ) : (
                            <><Icons.Upload size={22} className="text-teal-400" /><span className="text-xs text-stone-600">Upload {side === 'front' ? 'Front' : 'Back'}</span><span className="text-[10px] text-stone-400">Auto-fills via OCR</span></>
                          )}
                        </button>
                      )}
                      <input ref={side === 'front' ? editFrontRef : editBackRef} type="file" accept="image/*" onChange={e => handleEditIdImage(e, side)} className="hidden" />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                  {[['ID Number', p?.id_number], ['Issue Date', p?.id_issue_date], ['Issue Place', p?.id_issue_place]].map(([label, val]) => (
                    <div key={label} className="flex justify-between py-1.5"><span className="text-sm text-stone-500">{label}</span><span className="text-sm text-stone-900 font-medium">{val || 'N/A'}</span></div>
                  ))}
                </div>
                {(p?.id_front_image || p?.id_back_image) && (
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    {['front', 'back'].filter(s => p?.[`id_${s}_image`]).map(side => (
                      <div key={side}>
                        <span className="block text-xs font-medium text-stone-500 mb-1.5 capitalize">{side}</span>
                        <img src={p[`id_${side}_image`].startsWith('data:') ? p[`id_${side}_image`] : `data:image/jpeg;base64,${p[`id_${side}_image`]}`}
                          alt={`ID ${side}`} className="w-full h-40 object-contain rounded-lg border border-stone-200 bg-stone-50 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => window.open(p[`id_${side}_image`].startsWith('data:') ? p[`id_${side}_image`] : `data:image/jpeg;base64,${p[`id_${side}_image`]}`, '_blank')} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          {/* Address & Work */}
          <section>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Icons.MapPin size={14} className="text-green-500" />{t('renters:profile.section.addressWork')}
            </h3>
            {profileLoading ? <div className="text-sm text-stone-400">{t('renters:profile.section.loading')}</div> : isEditing ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className={LABEL_CLS}>{t('renters:profile.address')}</label><input type="text" value={profileForm.address} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))} className={INPUT_CLS} /></div>
                <div className="col-span-2"><label className={LABEL_CLS}>{t('renters:profile.placeOfOrigin')}</label><input type="text" value={profileForm.place_of_origin} onChange={e => setProfileForm(f => ({ ...f, place_of_origin: e.target.value }))} className={INPUT_CLS} /></div>
                <div className="col-span-2"><label className={LABEL_CLS}>{t('renters:profile.permanentAddress')}</label><input type="text" value={profileForm.permanent_address} onChange={e => setProfileForm(f => ({ ...f, permanent_address: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>{t('renters:profile.occupation')}</label><input type="text" value={profileForm.occupation} onChange={e => setProfileForm(f => ({ ...f, occupation: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>{t('renters:profile.workplace')}</label><input type="text" value={profileForm.workplace} onChange={e => setProfileForm(f => ({ ...f, workplace: e.target.value }))} className={INPUT_CLS} /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {[[t('renters:profile.address'), p?.address, true], [t('renters:profile.placeOfOrigin'), p?.place_of_origin, true], [t('renters:profile.permanentAddress'), p?.permanent_address, true], [t('renters:profile.occupation'), p?.occupation, false], [t('renters:profile.workplace'), p?.workplace, false]].map(([label, val, full]) => (
                  <div key={label} className={`flex justify-between py-1.5 ${full ? 'col-span-2' : ''}`}>
                    <span className="text-sm text-stone-500">{label}</span><span className="text-sm text-stone-900">{val || 'N/A'}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Emergency Contact */}
          <section>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Icons.Alert size={14} className="text-amber-500" />Emergency Contact
            </h3>
            {isEditing ? (
              <div className="grid grid-cols-3 gap-4">
                <div><label className={LABEL_CLS}>Contact Name</label><input type="text" value={profileForm.emergency_contact_name} onChange={e => setProfileForm(f => ({ ...f, emergency_contact_name: e.target.value }))} className={INPUT_CLS} /></div>
                <div><label className={LABEL_CLS}>Contact Phone</label><input type="text" value={profileForm.emergency_contact_phone} onChange={e => setProfileForm(f => ({ ...f, emergency_contact_phone: e.target.value }))} className={INPUT_CLS} /></div>
                <div>
                  <label className={LABEL_CLS}>Relationship</label>
                  <select value={profileForm.emergency_contact_relationship} onChange={e => setProfileForm(f => ({ ...f, emergency_contact_relationship: e.target.value }))} className={INPUT_CLS}>
                    <option value="">Select...</option>
                    {['parent', 'spouse', 'sibling', 'friend', 'other'].map(r => <option key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                {[['Name', profileForm.emergency_contact_name], ['Phone', profileForm.emergency_contact_phone], ['Relationship', profileForm.emergency_contact_relationship]].map(([label, val]) => (
                  <div key={label} className="flex justify-between py-1.5"><span className="text-sm text-stone-500">{label}</span><span className="text-sm text-stone-900 capitalize">{val || 'N/A'}</span></div>
                ))}
              </div>
            )}
          </section>

          {/* Notes */}
          <section>
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Icons.Document size={14} className="text-stone-500" />Notes
            </h3>
            {isEditing ? (
              <textarea value={profileForm.notes} onChange={e => setProfileForm(f => ({ ...f, notes: e.target.value }))}
                className={`${INPUT_CLS} h-auto resize-none`} rows={3} placeholder="Internal notes about this renter..." />
            ) : (
              <p className="text-sm text-stone-700">{profileForm.notes || <span className="text-stone-400">No notes</span>}</p>
            )}
          </section>

          {/* Rental Information */}
          {(renter.building_name || renter.contract_id) && (
            <section className="bg-teal-50 rounded-xl p-4 border border-teal-100">
              <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Icons.Home size={14} className="text-teal-500" />Rental Information
              </h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div className="flex justify-between py-1.5"><span className="text-sm text-stone-500">Building</span><span className="text-sm text-stone-900">{renter.building_name || 'Not assigned'}</span></div>
                <div className="flex justify-between py-1.5"><span className="text-sm text-stone-500">Room</span><span className="text-sm text-stone-900">{renter.room_number || 'Not assigned'}</span></div>
                {renter.contract_number && <div className="flex justify-between py-1.5"><span className="text-sm text-stone-500">Contract</span><span className="text-sm text-stone-900">{renter.contract_number}</span></div>}
                {renter.contract_status && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-sm text-stone-500">Status</span>
                    <Badge tone={renter.contract_status === 'active' ? 'success' : renter.contract_status === 'expired' ? 'danger' : 'warning'}>{renter.contract_status}</Badge>
                  </div>
                )}
                {renter.contract_start_date && <div className="flex justify-between py-1.5"><span className="text-sm text-stone-500">Start Date</span><span className="text-sm text-stone-900">{format(new Date(renter.contract_start_date), 'MMM dd, yyyy')}</span></div>}
                {renter.contract_end_date && <div className="flex justify-between py-1.5"><span className="text-sm text-stone-500">End Date</span><span className="text-sm text-stone-900">{format(new Date(renter.contract_end_date), 'MMM dd, yyyy')}</span></div>}
                {renter.monthly_rent && <div className="flex justify-between py-1.5"><span className="text-sm text-stone-500">Monthly Rent</span><span className="text-sm text-stone-900 font-semibold">₫{(renter.monthly_rent || 0).toLocaleString('vi-VN')}</span></div>}
              </div>
            </section>
          )}
        </div>

        <div className="border-t bg-stone-50 px-5 py-3 flex items-center justify-between">
          <div className="flex gap-3">
            {renter.contract_id && (
              <button onClick={handleViewContract} className="text-xs font-medium text-teal-600 hover:text-teal-800 flex items-center gap-1">
                <Icons.Document size={12} />Contract
              </button>
            )}
            <button onClick={handleViewTickets} className="text-xs font-medium text-teal-600 hover:text-teal-800 flex items-center gap-1">
              <Icons.Ticket size={12} />Tickets
            </button>
            <button onClick={handleViewPayments} className="text-xs font-medium text-teal-600 hover:text-teal-800 flex items-center gap-1">
              <Icons.Wallet size={12} />Payments
            </button>
          </div>
          {!renter.contract_id && !isEditing && (
            <button onClick={() => setShowDeleteConfirm(true)} className="text-xs font-medium text-red-600 hover:text-red-800 flex items-center gap-1">
              <Icons.Trash size={12} />Delete Renter
            </button>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="border-t bg-red-50 px-5 py-4">
            <div className="flex items-start gap-3">
              <Icons.Alert size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">Permanently delete {renter.full_name}?</p>
                <p className="text-xs text-red-600 mt-1">This action cannot be undone. All renter data will be removed.</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={handleDelete} disabled={deleteLoading} className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
                    {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 bg-white text-stone-700 text-xs font-medium rounded-lg border hover:bg-stone-50">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddRenterModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation(['renters', 'common']);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', id_number: '', date_of_birth: '', gender: '',
    address: '', place_of_origin: '', emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [idCardState, setIdCardState] = useState({ front: { preview: null, result: null, loading: false }, back: { preview: null, result: null, loading: false } });
  const frontFileInputRef = React.useRef(null);
  const backFileInputRef = React.useRef(null);

  const mergeOcrResults = (frontData, backData) => ({
    id_number: frontData?.id_number || backData?.id_number,
    full_name: frontData?.full_name || backData?.full_name,
    date_of_birth: frontData?.date_of_birth || backData?.date_of_birth,
    gender: frontData?.gender || backData?.gender,
    address: frontData?.address || backData?.address,
    place_of_origin: frontData?.place_of_origin || backData?.place_of_origin,
    issue_date: backData?.issue_date || frontData?.issue_date,
    expiry_date: backData?.expiry_date || frontData?.expiry_date,
  });

  const applyOcrToForm = (ocrData) => {
    if (!ocrData) return;
    let firstName = '', lastName = '';
    if (ocrData.full_name) {
      const nameParts = ocrData.full_name.trim().split(' ');
      firstName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : ocrData.full_name;
      lastName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : '';
    }
    let dob = '';
    if (ocrData.date_of_birth) {
      const parts = ocrData.date_of_birth.split(/[\/\-\.]/);
      if (parts.length === 3) dob = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    setFormData(prev => ({
      ...prev,
      first_name: firstName || prev.first_name, last_name: lastName || prev.last_name,
      id_number: ocrData.id_number || prev.id_number, date_of_birth: dob || prev.date_of_birth,
      gender: ocrData.gender === 'Nam' ? 'male' : ocrData.gender === 'Nữ' ? 'female' : prev.gender,
      address: ocrData.address || prev.address, place_of_origin: ocrData.place_of_origin || prev.place_of_origin,
    }));
  };

  const handleIdCardSelect = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('renters:toast.imageRequired')); return; }
    const reader = new FileReader();
    reader.onloadend = () => setIdCardState(prev => ({ ...prev, [side]: { ...prev[side], preview: reader.result } }));
    reader.readAsDataURL(file);
    setIdCardState(prev => ({ ...prev, [side]: { ...prev[side], loading: true } }));
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('side', side);
      const response = await api.post('/api/ocr/vietnamese-id', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data?.success && response.data?.data) {
        setIdCardState(prev => {
          const newState = { ...prev, [side]: { ...prev[side], result: response.data, loading: false } };
          applyOcrToForm(mergeOcrResults(newState.front.result?.data, newState.back.result?.data));
          return newState;
        });
        toast.success(`${side === 'front' ? 'Front' : 'Back'} side extracted successfully!`);
      } else { setIdCardState(prev => ({ ...prev, [side]: { ...prev[side], loading: false } })); }
    } catch { setIdCardState(prev => ({ ...prev, [side]: { ...prev[side], loading: false } })); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) { toast.error(t('renters:toast.nameRequired')); return; }
    if (!formData.email && !formData.phone) { toast.error(t('renters:toast.contactRequired')); return; }
    setIsLoading(true);
    try {
      const payload = {
        first_name: formData.first_name, last_name: formData.last_name,
        email: formData.email || null, phone: formData.phone || null,
        id_number: formData.id_number || null, date_of_birth: formData.date_of_birth || null,
        gender: formData.gender || null, address: formData.address || null,
        place_of_origin: formData.place_of_origin || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        emergency_contact_relationship: formData.emergency_contact_relationship || null,
        password: 'admin123',
      };
      if (idCardState.front.preview) payload.id_front_image = idCardState.front.preview;
      if (idCardState.back.preview) payload.id_back_image = idCardState.back.preview;
      const response = await api.post('/api/users/renters', payload);
      if (response.data) { toast.success(t('renters:toast.renterCreated')); onSuccess?.(); onClose(); }
    } catch (error) { toast.error(error.response?.data?.detail || t('renters:toast.createFailed')); }
    finally { setIsLoading(false); }
  };

  const F = (label, field, type = 'text', placeholder = '') => (
    <div key={field}>
      <label className="block text-sm font-medium text-stone-700 mb-1">{label}</label>
      <input type={type} value={formData[field]} onChange={e => setFormData(d => ({ ...d, [field]: e.target.value }))}
        className={INPUT_CLS} placeholder={placeholder} />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto ring-1 ring-black/5">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg"><Icons.Users size={22} className="text-teal-600" /></div>
            <div><h2 className="text-xl font-bold text-stone-900">{t('renters:modal.addNew')}</h2><p className="text-sm text-stone-500">{t('renters:modal.addNewSubtitle')}</p></div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg"><Icons.X size={22} className="text-stone-500" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* ID Card OCR */}
          <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-5 border border-teal-100">
            <div className="flex items-center gap-3 mb-4">
              <Icons.CreditCard size={22} className="text-teal-600" />
              <div>
                <h3 className="font-semibold text-stone-900">ID Card OCR (Optional)</h3>
                <p className="text-sm text-stone-600">Upload both sides of ID card for complete information</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {['front', 'back'].map(side => (
                <div key={side} className={`bg-white rounded-lg p-3 border ${side === 'front' ? 'border-teal-200' : 'border-blue-200'}`}>
                  <div className="text-center mb-2">
                    <span className={`text-sm font-medium ${side === 'front' ? 'text-teal-600' : 'text-blue-600'}`}>{side.toUpperCase()} SIDE</span>
                    <p className="text-xs text-stone-500">{side === 'front' ? 'ID, Name, DOB, Gender' : 'Issue Date, Expiry Date'}</p>
                  </div>
                  {idCardState[side].preview ? (
                    <div className="relative">
                      <img src={idCardState[side].preview} alt={`${side} preview`} className={`w-full h-28 object-cover rounded-lg border-2 ${side === 'front' ? 'border-teal-300' : 'border-blue-300'}`} />
                      <button type="button" onClick={() => { setIdCardState(prev => ({ ...prev, [side]: { preview: null, result: null, loading: false } })); if (side === 'front' && frontFileInputRef.current) frontFileInputRef.current.value = ''; if (side === 'back' && backFileInputRef.current) backFileInputRef.current.value = ''; }}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                        <Icons.X size={12} />
                      </button>
                      {idCardState[side].result && <div className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-2 py-0.5 rounded">{Math.round((idCardState[side].result.confidence || 0) * 100)}%</div>}
                    </div>
                  ) : (
                    <button type="button" onClick={() => (side === 'front' ? frontFileInputRef : backFileInputRef).current?.click()} disabled={idCardState[side].loading}
                      className={`w-full h-28 border-2 border-dashed ${side === 'front' ? 'border-teal-300 hover:border-teal-500 hover:bg-teal-50' : 'border-blue-300 hover:border-blue-500 hover:bg-blue-50'} rounded-lg flex flex-col items-center justify-center gap-1 transition disabled:opacity-50`}>
                      {idCardState[side].loading ? (
                        <><Icons.Spinner size={22} className={`animate-spin ${side === 'front' ? 'text-teal-600' : 'text-blue-600'}`} /><span className="text-xs text-stone-600">Processing...</span></>
                      ) : (
                        <><Icons.Upload size={22} className={side === 'front' ? 'text-teal-400' : 'text-blue-400'} /><span className="text-xs text-stone-600">Upload {side === 'front' ? 'Front' : 'Back'}</span></>
                      )}
                    </button>
                  )}
                  <input ref={side === 'front' ? frontFileInputRef : backFileInputRef} type="file" accept="image/*" capture="environment"
                    onChange={e => handleIdCardSelect(e, side)} className="hidden" />
                </div>
              ))}
            </div>
            {(idCardState.front.result || idCardState.back.result) ? (
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <Icons.Sparkles size={16} /><span className="font-medium">OCR Extracted</span>
                  <span className="text-xs text-stone-500">({idCardState.front.result ? 'Front' : ''}{idCardState.front.result && idCardState.back.result ? ' + ' : ''}{idCardState.back.result ? 'Back' : ''})</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                  {idCardState.front.result?.data?.id_number && <p><span className="font-medium">ID:</span> {idCardState.front.result.data.id_number}</p>}
                  {idCardState.front.result?.data?.full_name && <p><span className="font-medium">Name:</span> {idCardState.front.result.data.full_name}</p>}
                  {idCardState.front.result?.data?.date_of_birth && <p><span className="font-medium">DOB:</span> {idCardState.front.result.data.date_of_birth}</p>}
                  {idCardState.back.result?.data?.issue_date && <p><span className="font-medium">Issue Date:</span> {idCardState.back.result.data.issue_date}</p>}
                </div>
              </div>
            ) : <div className="text-sm text-stone-500 text-center"><p>Supported: Vietnamese CCCD / CMND</p></div>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {F('First Name *', 'first_name', 'text', 'Enter first name')}
            {F('Last Name *', 'last_name', 'text', 'Enter last name')}
            {F('Email', 'email', 'email', 'email@example.com')}
            {F('Phone', 'phone', 'tel', '0123456789')}
            {F('ID Number (CCCD/CMND)', 'id_number', 'text', 'ID number')}
            {F('Date of Birth', 'date_of_birth', 'date')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Gender</label>
              <select value={formData.gender} onChange={e => setFormData(d => ({ ...d, gender: e.target.value }))} className={INPUT_CLS}>
                <option value="">Select gender</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </div>
            {F('Place of Origin', 'place_of_origin', 'text', 'Place of origin')}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Current Address</label>
            <textarea value={formData.address} onChange={e => setFormData(d => ({ ...d, address: e.target.value }))}
              className={`${INPUT_CLS} h-20 resize-none`} placeholder="Enter address" />
          </div>

          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
              <Icons.Alert size={18} className="text-amber-600" />Emergency Contact
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {F('Contact Name', 'emergency_contact_name', 'text', 'Full name')}
              {F('Contact Phone', 'emergency_contact_phone', 'tel', 'Phone number')}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Relationship</label>
                <select value={formData.emergency_contact_relationship} onChange={e => setFormData(d => ({ ...d, emergency_contact_relationship: e.target.value }))} className={INPUT_CLS}>
                  <option value="">Select...</option>
                  {['parent', 'spouse', 'sibling', 'child', 'friend', 'other'].map(r => <option key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
            <Button kind="secondary" type="button" onClick={onClose} disabled={isLoading}>Cancel</Button>
            <Button kind="primary" type="submit" loading={isLoading} iconLeft={!isLoading ? <Icons.Plus /> : undefined}>
              {isLoading ? 'Creating...' : 'Create Renter'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Renters;
